import { RiImage2Line } from '@remixicon/react'
import { useEffect, useState } from 'react'

import { Skeleton } from '../ui/skeleton'

import { cn } from '@/lib/utils'
import { isPresignedUrl } from '@/utils/url'

interface ImageWithSkeletonProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  className?: string
}

export const ImageWithFallback = (props: ImageWithSkeletonProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  // Reset on src change so a new (e.g. cache-busted) URL retries instead of
  // staying stuck on a prior transient error.
  useEffect(() => {
    setIsLoading(true)
    setHasError(false)
  }, [props.src])

  const handleImageLoad = () => {
    setIsLoading(false)
  }

  const handleImageError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  if (hasError || !props.src)
    return (
      <div className={cn('flex h-34.5 shrink-0 flex-col items-center justify-center gap-2', props.className)}>
        <RiImage2Line className='text-grey-300 size-8' />
        <span className='text-grey-500 text-xs'>Image Not Found</span>
      </div>
    )

  return (
    <>
      {isLoading && <Skeleton className={props.className} />}
      <img
        // A signed R2 link is the same URL the image editor loads into a canvas
        // with CORS; fetching it CORS-mode here too keeps a non-CORS copy out of
        // the browser cache, which would otherwise block the editor's request.
        crossOrigin={isPresignedUrl(props.src) ? 'anonymous' : undefined}
        {...props}
        className={cn(props.className, isLoading || hasError ? 'hidden' : '')}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </>
  )
}
